// Web3Service.js
import Web3 from 'web3';
import { BSC_TESTNET, WS_PROVIDER_URL, COINFLIP_CONTRACT_ADDRESS, PRIORITY_FEE_GWEI } from '../config';
import contractABI from '../contractABI';

/* global BigInt */  // This tells ESLint that BigInt is defined globally

class Web3Service {
  constructor() {
    this.web3 = null;
    this.web3Ws = null;
    this.contract = null;
    this.contractWs = null;
    this.account = null;
    this.connectionStatus = 'Connecting...';
    this.eventSubscriptions = [];
  }

  // Detect available wallet providers
  detectWalletProviders() {
    const providers = [];
    
    // Check for MetaMask specifically - use window.ethereum.providers if available
    if (window.ethereum?.providers) {
      // Newer MetaMask versions expose providers array
      const metaMaskProvider = window.ethereum.providers.find(p => p.isMetaMask && !p.isPhantom);
      if (metaMaskProvider) {
        providers.push({
          name: 'MetaMask',
          provider: metaMaskProvider
        });
      }
    } else if (window.ethereum?.isMetaMask && !window.ethereum?.isPhantom) {
      // Older MetaMask versions or when providers array is not available
      providers.push({
        name: 'MetaMask',
        provider: window.ethereum
      });
    }
    
    // Check for Phantom specifically - prefer phantom.ethereum
    if (window.phantom?.solana) {
      const phantomProvider = window.phantom.ethereum || 
                             (window.ethereum?.isPhantom ? window.ethereum : null);
      if (phantomProvider) {
        providers.push({
          name: 'Phantom',
          provider: phantomProvider
        });
      }
    } else if (window.ethereum?.isPhantom) {
      // Fallback to window.ethereum if it has isPhantom flag
      providers.push({
        name: 'Phantom',
        provider: window.ethereum
      });
    }
    
    // If no specific providers found but window.ethereum exists, add as generic
    if (providers.length === 0 && window.ethereum) {
      providers.push({
        name: 'Default',
        provider: window.ethereum
      });
    }
    
    return providers;
  }

  // Get the preferred provider (MetaMask if available)
  getPreferredProvider() {
    // First, try to directly access MetaMask from providers array (most reliable)
    if (window.ethereum?.providers) {
      const metaMaskProvider = window.ethereum.providers.find(p => p.isMetaMask && !p.isPhantom);
      if (metaMaskProvider) {
        return metaMaskProvider;
      }
    }
    
    // Then try the regular detection method
    const providers = this.detectWalletProviders();
    
    // Return MetaMask if it's available (prioritize it)
    const metaMask = providers.find(p => p.name === 'MetaMask');
    if (metaMask) {
      return metaMask.provider;
    }
    
    // Fall back to the first available provider
    if (providers.length > 0) {
      return providers[0].provider;
    }
    
    return null;
  }

  hasMultipleProviders() {
    // Check for providers array (MetaMask and other wallets may expose this)
    if (window.ethereum?.providers && window.ethereum.providers.length > 1) {
      return true;
    }
    
    // Check for common wallet combinations
    let walletCount = 0;
    
    if (window.ethereum?.isMetaMask) walletCount++;
    if (window.ethereum?.isPhantom || window.phantom?.solana) walletCount++;
    if (window.ethereum?.isCoinbaseWallet) walletCount++;
    if (window.ethereum?.isTrust) walletCount++;
    
    return walletCount > 1;
  }

  // Initialize Web3 connection
  async initWeb3() {
    // Log provider detection for debugging
    console.log('Detecting providers:');
    console.log('Multiple providers detected:', this.hasMultipleProviders());
    console.log('ethereum.providers:', window.ethereum?.providers);
    console.log('ethereum.isMetaMask:', window.ethereum?.isMetaMask);
    console.log('ethereum.isPhantom:', window.ethereum?.isPhantom);
    console.log('phantom.solana exists:', !!window.phantom?.solana);
    
    // Get the preferred provider (MetaMask)
    const provider = this.getPreferredProvider();
    
    if (!provider) {
      this.connectionStatus = 'No Web3 Provider';
      throw new Error('Please install MetaMask to use this app');
    }
    
    try {
      // Store the current provider
      this.currentProvider = provider;
      
      // Set up HTTP provider with the selected provider
      this.web3 = new Web3(provider);
      
      // Request account access
      const accounts = await provider.request({ method: 'eth_requestAccounts' });
      this.account = accounts[0];
      
      // Check and switch network if needed
      await this.ensureCorrectNetwork();
      
      // Set up WebSocket provider for events
      await this.setupWebsocketProvider();
      
      // Initialize contract instances
      this.contract = new this.web3.eth.Contract(contractABI, COINFLIP_CONTRACT_ADDRESS);
      
      if (this.web3Ws) {
        this.contractWs = new this.web3Ws.eth.Contract(contractABI, COINFLIP_CONTRACT_ADDRESS);
      }
      
      // Set up account change listener
      provider.on('accountsChanged', (newAccounts) => {
        this.account = newAccounts[0];
      });
      
      // Set up chain change listener
      provider.on('chainChanged', () => {
        window.location.reload();
      });
      
      this.connectionStatus = 'Connected';
      return true;
    } catch (error) {
      console.error('Web3 initialization error:', error);
      this.connectionStatus = 'Connection Error';
      throw error;
    }
  }
  
  // Ensure the correct network is selected
  async ensureCorrectNetwork() {
    const chainId = await this.web3.eth.getChainId();
    if (chainId !== 97) { // BSC Testnet
      try {
        await this.currentProvider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: '0x61' }]
        });
      } catch (switchError) {
        if (switchError.code === 4902) {
          try {
            await this.currentProvider.request({
              method: 'wallet_addEthereumChain',
              params: [BSC_TESTNET]
            });
          } catch (addError) {
            console.error('Error adding BSC Testnet:', addError);
            throw new Error('Please add BSC Testnet to your MetaMask manually');
          }
        } else {
          throw switchError;
        }
      }
    }
  }
  
  // Set up WebSocket provider
  async setupWebsocketProvider() {
    try {
      // Create a new WebSocket provider with reconnection handling
      const wsProvider = new Web3.providers.WebsocketProvider(WS_PROVIDER_URL, {
        reconnect: {
          auto: true,
          delay: 2500, // ms
          maxAttempts: 5
        },
        clientConfig: {
          keepalive: true,
          keepaliveInterval: 30000 // ms
        }
      });
      
      this.web3Ws = new Web3(wsProvider);
      
      wsProvider.on('connect', () => {
        console.log('WebSocket connected');
        this.connectionStatus = 'Connected';
        
        // Reinitialize WebSocket contract instance
        if (this.web3Ws && COINFLIP_CONTRACT_ADDRESS) {
          this.contractWs = new this.web3Ws.eth.Contract(contractABI, COINFLIP_CONTRACT_ADDRESS);
          console.log('WebSocket contract initialized');
        }
      });
      
      wsProvider.on('error', (error) => {
        console.error('WebSocket error:', error);
      });
      
      wsProvider.on('close', (event) => {
        console.log('WebSocket disconnected - attempting reconnect', event);
        this.connectionStatus = 'WebSocket Disconnected - Reconnecting...';
        
        // Store the provider for reference
        this.wsProvider = wsProvider;
      });
      
      // Store the provider for reference
      this.wsProvider = wsProvider;
      
      return true;
    } catch (wsError) {
      console.error('WebSocket setup failed:', wsError);
      this.connectionStatus = 'Using HTTP Only';
      return false;
    }
  }

   // Get available wallet providers for UI display
   getAvailableWalletProviders() {
    return this.detectWalletProviders();
  }
  
  // Change wallet provider
  async changeWalletProvider(providerName) {
    // Special case for MetaMask - try to find it in providers array first
    if (providerName === 'MetaMask' && window.ethereum?.providers) {
      const metaMaskProvider = window.ethereum.providers.find(p => p.isMetaMask && !p.isPhantom);
      if (metaMaskProvider) {
        // Clean up current connections
        this.cleanup();
        
        // Update the current provider
        this.currentProvider = metaMaskProvider;
        
        // Reinitialize with the MetaMask provider
        return this.initWeb3();
      }
    }
    
    // Standard approach for other providers
    const providers = this.detectWalletProviders();
    const selectedProvider = providers.find(p => p.name === providerName);
    
    if (!selectedProvider) {
      throw new Error(`Provider ${providerName} not found`);
    }
    
    // Clean up current connections
    this.cleanup();
    
    // Update the current provider
    this.currentProvider = selectedProvider.provider;
    
    // Reinitialize with the new provider
    return this.initWeb3();
  }
  
  // Helper method to add priority fee to transaction options
  async addPriorityFee(txOptions, priorityFeeGwei = PRIORITY_FEE_GWEI) {
    if (!this.web3) {
      return txOptions;
    }
    
    try {
      // Get current gas price
      const gasPrice = await this.web3.eth.getGasPrice();
      
      // Convert priority fee to wei
      const priorityFeeWei = this.web3.utils.toWei(priorityFeeGwei.toString(), 'gwei');
      
      // Calculate max fee per gas (base fee + priority fee)
      // Use window.BigInt for better compatibility
      const maxFeePerGas = (window.BigInt(gasPrice) + window.BigInt(priorityFeeWei)).toString();
      
      console.log(`Adding priority fee of ${priorityFeeGwei} Gwei`);
      console.log(`Base gas price: ${this.web3.utils.fromWei(gasPrice, 'gwei')} Gwei`);
      console.log(`Max fee per gas: ${this.web3.utils.fromWei(maxFeePerGas, 'gwei')} Gwei`);
      
      // Add EIP-1559 gas params
      return {
        ...txOptions,
        maxFeePerGas,
        maxPriorityFeePerGas: priorityFeeWei
      };
    } catch (error) {
      console.error('Error adding priority fee:', error);
      // If there's an error, return the original options
      return txOptions;
    }
  }
  
  // Get contract data (balance and max bet)
  async getContractData() {
    if (!this.contract) {
      throw new Error('Contract not initialized');
    }
    
    try {
      const balance = await this.contract.methods.getHouseBalance().call();
      const max = await this.contract.methods.getMaxBet().call();
      const vrfFee = await this.contract.methods.calculateVRFFee().call();
      
      return { balance, max, vrfFee };
    } catch (error) {
      console.error('Error getting contract data:', error);
      throw error;
    }
  }
  
  // Check if user is the contract owner
  async checkIfOwner() {
    if (!this.contract || !this.account) {
      return false;
    }
    
    try {
      const ownerAddress = await this.contract.methods.owner().call();
      return this.account.toLowerCase() === ownerAddress.toLowerCase();
    } catch (error) {
      console.error('Error checking owner status:', error);
      return false;
    }
  }
  
  // Check if user has a pending bet
  async hasPendingBet() {
    if (!this.contract || !this.account) {
      return false;
    }
    
    try {
      return await this.contract.methods.hasPendingBet(this.account).call();
    } catch (error) {
      console.error('Error checking pending bet:', error);
      throw error;
    }
  }
  
  // Place a bet
  async placeBet(choice, betAmount, totalAmount) {
    if (!this.contract || !this.account) {
      throw new Error('Contract or account not initialized');
    }
    
    try {
      // Debug logs for choice parameter
      console.log('Web3Service placeBet called with choice:', choice, 
        'which is', choice === 0 ? 'Heads' : choice === 1 ? 'Tails' : 'Invalid');
      console.log('Type of choice parameter:', typeof choice);
      
      // Convert choice to number to ensure it's properly formatted
      const choiceValue = Number(choice);
      console.log('Converted choice value:', choiceValue);
      
      // Estimate gas to avoid errors
      const gasEstimate = await this.contract.methods.placeBet(choiceValue).estimateGas({
        from: this.account,
        value: totalAmount
      });
      
      const gasLimit = Math.ceil(Number(gasEstimate) * 1.2); // Add 20% buffer
      
      // Basic transaction options
      const txOptions = {
        from: this.account,
        value: totalAmount,
        gas: gasLimit
      };
      
      // Add priority fee to transaction options
      const txOptionsWithPriorityFee = await this.addPriorityFee(txOptions);
      
      // Log transaction before submitting
      console.log('About to submit transaction with choice:', choiceValue);
      
      // Place the bet with priority fee
      const receipt = await this.contract.methods.placeBet(choiceValue).send(txOptionsWithPriorityFee);
      
      // Check for BetPlaced event in receipt
      if (receipt.events && receipt.events.BetPlaced) {
        console.log('BetPlaced event in receipt:', receipt.events.BetPlaced);
        console.log('Choice in event:', receipt.events.BetPlaced.returnValues.choice);
        
        return {
          success: true,
          requestId: receipt.events.BetPlaced.returnValues.requestId,
          txHash: receipt.transactionHash
        };
      }
      
      return { 
        success: true,
        txHash: receipt.transactionHash
      };
    } catch (error) {
      console.error('Error placing bet:', error);
      throw error;
    }
  }
  // Add liquidity (owner only)
  async addLiquidity(amount) {
    if (!this.contract || !this.account) {
      throw new Error('Contract or account not initialized');
    }
    
    try {
      // Basic transaction options
      const txOptions = {
        from: this.account,
        value: amount,
        gas: 300000
      };
      
      // Add priority fee to transaction options
      const txOptionsWithPriorityFee = await this.addPriorityFee(txOptions);
      
      const receipt = await this.contract.methods.addLiquidity().send(txOptionsWithPriorityFee);
      
      return {
        success: true,
        txHash: receipt.transactionHash
      };
    } catch (error) {
      console.error('Error adding liquidity:', error);
      throw error;
    }
  }
  
  // Remove liquidity (owner only)
  async removeLiquidity(amount) {
    if (!this.contract || !this.account) {
      throw new Error('Contract or account not initialized');
    }
    
    try {
      // Basic transaction options
      const txOptions = {
        from: this.account,
        gas: 300000
      };
      
      // Add priority fee to transaction options
      const txOptionsWithPriorityFee = await this.addPriorityFee(txOptions);
      
      const receipt = await this.contract.methods.removeLiquidity(amount).send(txOptionsWithPriorityFee);
      
      return {
        success: true,
        txHash: receipt.transactionHash
      };
    } catch (error) {
      console.error('Error removing liquidity:', error);
      throw error;
    }
  }
  
  // Subscribe to contract events
  subscribeToEvents(onBetPlaced, onBetSettled) {
    // Clear any existing subscriptions
    this.clearEventSubscriptions();
    
    // Use websocket contract for events if available, otherwise use HTTP
    // IMPORTANT FIX: Check if contract instance and account exist
    if (!this.contract || !this.account) {
      console.log('Cannot subscribe to events: Contract or account not initialized');
      return false;
    }
    
    try {
      // First, check if the websocket provider is actually connected
      const eventContract = (this.web3Ws && this.contractWs) ? this.contractWs : this.contract;
      
      console.log('Subscribing to events using:', 
                 (this.web3Ws && this.contractWs) ? 'WebSocket provider' : 'HTTP provider');
      
      // Safely create event subscriptions
      let betPlacedSub = null;
      let betSettledSub = null;
      
      try {
        // BetPlaced event
        betPlacedSub = eventContract.events.BetPlaced({ filter: { player: this.account } })
          .on('data', event => {
            console.log('🎲 BetPlaced event received:', event);
            if (onBetPlaced && typeof onBetPlaced === 'function') {
              onBetPlaced(event);
            }
          })
          .on('error', error => {
            console.error('❌ BetPlaced event error:', error);
          });
      } catch (placeError) {
        console.error('Error subscribing to BetPlaced events:', placeError);
        betPlacedSub = null;
      }
      
      try {
        // BetSettled event
        betSettledSub = eventContract.events.BetSettled({ filter: { player: this.account } })
          .on('data', event => {
            console.log('💰 BetSettled event received:', event);
            if (onBetSettled && typeof onBetSettled === 'function') {
              onBetSettled(event);
            }
          })
          .on('error', error => {
            console.error('❌ BetSettled event error:', error);
          });
      } catch (settleError) {
        console.error('Error subscribing to BetSettled events:', settleError);
        betSettledSub = null;
      }
      
      // Only store valid subscriptions
      if (betPlacedSub) this.eventSubscriptions.push(betPlacedSub);
      if (betSettledSub) this.eventSubscriptions.push(betSettledSub);
      
      // Return success if at least one subscription was created
      return (betPlacedSub !== null || betSettledSub !== null);
    } catch (error) {
      console.error('Error subscribing to events:', error);
      return false;
    }
  }
  
  // Get past bet settled events
  async getPastBetSettledEvents(requestId, fromBlock = 'latest') {
    if (!this.contract || !this.account) {
      throw new Error('Contract or account not initialized');
    }
    
    try {
      // If fromBlock is 'latest', try to get the current block number and look back
      if (fromBlock === 'latest') {
        const currentBlock = await this.web3.eth.getBlockNumber();
        // Convert to number to avoid BigInt issues
        const blockNumber = typeof currentBlock === 'bigint' ? Number(currentBlock) : currentBlock;
        fromBlock = Math.max(0, blockNumber - 5000);
      }
      
      const events = await this.contract.getPastEvents('BetSettled', {
        filter: { player: this.account },
        fromBlock: fromBlock,
        toBlock: 'latest'
      });
      
      if (requestId) {
        // Convert requestId to string for comparison to avoid BigInt mixing issues
        const requestIdStr = requestId.toString();
        
        // Find the event that matches our requestId
        return events.find(event => 
          event.returnValues.requestId.toString() === requestIdStr
        );
      }
      
      return events;
    } catch (error) {
      console.error('Error getting past events:', error);
      throw error;
    }
  }
  
  // Get transaction receipt
  async getTransactionReceipt(txHash) {
    if (!this.web3 || !txHash) {
      throw new Error('Web3 not initialized or invalid transaction hash');
    }
    
    try {
      return await this.web3.eth.getTransactionReceipt(txHash);
    } catch (error) {
      console.error('Error getting transaction receipt:', error);
      throw error;
    }
  }
  
  // Clean up event subscriptions
  clearEventSubscriptions() {
    for (const subscription of this.eventSubscriptions) {
      if (subscription && subscription.unsubscribe) {
        try {
          subscription.unsubscribe();
        } catch (error) {
          console.error('Error unsubscribing from event:', error);
        }
      }
    }
    this.eventSubscriptions = [];
  }
  
  // Clean up resources
  cleanup() {
    this.clearEventSubscriptions();
    
    if (this.currentProvider) {
      this.currentProvider.removeAllListeners('accountsChanged');
      this.currentProvider.removeAllListeners('chainChanged');
    }
  }
  
  // Format utility
  fromWei(value) {
    return this.web3 ? this.web3.utils.fromWei(value, 'ether') : '0';
  }
  
  // Format account address
  formatAddress(address = this.account) {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }
  
  // Check if the network is connected
  isConnected() {
    return this.web3 !== null && this.account !== null;
  }
}

// Create and export a singleton instance
const web3Service = new Web3Service();
export default web3Service;